import { Link, useLocation } from "react-router-dom"
import { LayoutDashboard, Flame, CalendarDays, ChevronLeft, ChevronRight } from "lucide-react"
import { useState } from "react"
import { cn } from "@/lib/utils"

const NAV_ITEMS = [
	{ label: "Dashboard", icon: LayoutDashboard, to: "/dashboard" },
	{ label: "FIRE", icon: Flame, to: "/fire" },
	{ label: "Calendar", icon: CalendarDays, to: "/calendar" },
]

const STORAGE_KEY = "beediv-sidebar-collapsed"

interface SidebarProps {
	/** When true, renders inside a mobile Sheet — no collapse toggle, always expanded */
	mobile?: boolean
	/** Called when a nav item is clicked (used to close the mobile drawer) */
	onNavigate?: () => void
}

export function Sidebar({ mobile = false, onNavigate }: SidebarProps) {
	const location = useLocation()
	const [collapsed, setCollapsed] = useState(() => {
		if (mobile) return false
		try {
			return localStorage.getItem(STORAGE_KEY) === "true"
		} catch {
			return false
		}
	})

	const toggleCollapsed = () => {
		const next = !collapsed
		setCollapsed(next)
		try {
			localStorage.setItem(STORAGE_KEY, String(next))
		} catch {}
	}

	return (
		<div
			className={cn(
				"flex flex-col h-full bg-white/40 dark:bg-black/40 backdrop-blur-md border-r border-sky-900/10 dark:border-sky-100/10 transition-all duration-300",
				mobile ? "w-56" : collapsed ? "w-16" : "w-52",
			)}
		>
			{/* Nav items */}
			<nav className="flex-1 px-2 py-4 space-y-1">
				{NAV_ITEMS.map(({ label, icon: Icon, to }) => {
					const isActive = location.pathname === to
					return (
						<Link
							key={to}
							to={to}
							onClick={onNavigate}
							title={collapsed && !mobile ? label : undefined}
							className={cn(
								"flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
								isActive
									? "bg-sky-100 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300"
									: "text-sky-900/70 dark:text-sky-100/70 hover:bg-sky-100/60 dark:hover:bg-sky-900/20 hover:text-sky-900 dark:hover:text-sky-100",
								collapsed && !mobile && "justify-center px-2",
							)}
						>
							<Icon className="h-5 w-5 shrink-0" />
							{(!collapsed || mobile) && <span>{label}</span>}
						</Link>
					)
				})}
			</nav>

			{/* Collapse toggle — desktop only */}
			{!mobile && (
				<div className="px-2 pb-4">
					<button
						onClick={toggleCollapsed}
						className="w-full flex items-center justify-center p-2 rounded-lg text-sky-900/50 dark:text-sky-100/50 hover:bg-sky-100/60 dark:hover:bg-sky-900/20 hover:text-sky-900 dark:hover:text-sky-100 transition-colors"
						aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
					>
						{collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
					</button>
				</div>
			)}
		</div>
	)
}
